// src/utils/date.ts

import { format } from 'date-fns';

/**
 * Format a date string for display.
 * Handles various date formats: YYYY-MM-DD, ISO strings, etc.
 * When format includes time components (H, m, s), preserves the full timestamp.
 * Otherwise, avoids timezone conversion issues by parsing dates locally.
 */
export function formatDate(
  dateString?: string | null,
  formatStr: string = 'MMM dd, yyyy'
): string {
  if (!dateString || dateString.trim() === '') return 'N/A';
  
  // Check if format includes time components
  const includesTime = /[Hms]/.test(formatStr);
  
  try {
    // Handle date-only strings (YYYY-MM-DD) without timezone conversion
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateString.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      if (isNaN(date.getTime())) return 'Invalid date';
      return format(date, formatStr);
    }
    
    // Handle ISO strings
    if (dateString.includes('T')) {
      // If format includes time, parse full ISO string (displays in local timezone)
      if (includesTime) {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'Invalid date';
        return format(date, formatStr);
      }
      
      // For date-only formats, extract date part to avoid timezone shifts
      const dateOnly = dateString.split('T')[0];
      const [year, month, day] = dateOnly.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      if (isNaN(date.getTime())) return 'Invalid date';
      return format(date, formatStr);
    }
    
    // Fallback to standard Date parsing
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid date';
    return format(date, formatStr);
  } catch {
    return 'Invalid date';
  }
}

/**
 * Format a duration in seconds to a human-readable string.
 * Example: 3661 -> "1h 1m 1s"
 */
export function formatDuration(seconds?: number | null): string {
  if (seconds === undefined || seconds === null) return 'N/A';
  
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  
  return `${hrs}h ${mins}m ${secs}s`;
}

/**
 * Parse a duration string back to seconds.
 * Handles formats like: "1h 30m 45s", "2h", "45m", etc.
 */
export function parseDuration(durationStr: string): number | null {
  if (!durationStr || durationStr.trim() === '') return null;
  
  // Try to match the format: Xh Xm Xs or variations
  const match = durationStr.trim().match(/(?:(\d+)h)?\s*(?:(\d+)m)?\s*(?:(\d+)s)?/i);
  if (match) {
    const hrs = parseInt(match[1] || '0', 10);
    const mins = parseInt(match[2] || '0', 10);
    const secs = parseInt(match[3] || '0', 10);
    
    // Only return if at least one value was found
    if (hrs > 0 || mins > 0 || secs > 0) {
      return hrs * 3600 + mins * 60 + secs;
    }
  }
  
  return null;
}

/**
 * Format a date for datetime-local input (YYYY-MM-DDTHH:mm)
 */
export function formatForDateTimeInput(dateString?: string | null): string {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  } catch {
    return '';
  }
}

/**
 * Format a date for date input (YYYY-MM-DD)
 */
export function formatForDateInput(dateString?: string | null): string {
  if (!dateString) return '';
  
  try {
    // If already in YYYY-MM-DD format, return as-is
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateString;
    }
    
    // Handle ISO strings
    if (dateString.includes('T')) {
      return dateString.split('T')[0];
    }
    
    // Parse and format
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  } catch {
    return '';
  }
}

/**
 * Format a duration in seconds to HH:MM:SS format.
 * Example: 3661 -> "01:01:01", 125 -> "00:02:05"
 */
export function formatDurationHHMMSS(seconds: number | undefined | null): string {
  if (seconds === undefined || seconds === null || seconds === 0) return '00:00:00';
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Parse a duration string to seconds.
 * Handles formats: HH:MM:SS, MM:SS, or plain number (seconds).
 * Example: "01:30:00" -> 5400, "02:05" -> 125, "3600" -> 3600
 */
export function parseDurationHHMMSS(durationString: string): number | undefined {
  if (!durationString || durationString.trim() === '') return undefined;
  const parts = durationString.trim().split(':').map(Number);
  if (parts.length === 2) {
    // MM:SS format
    const [mins, secs] = parts;
    return mins * 60 + secs;
  } else if (parts.length === 3) {
    // HH:MM:SS format
    const [hrs, mins, secs] = parts;
    return hrs * 3600 + mins * 60 + secs;
  }
  // Try to parse as just a number (seconds)
  const num = Number(durationString);
  return isNaN(num) ? undefined : num;
}

